import { type ComponentProps } from 'react'
import { cn } from '@/lib/utils'
import { FAIcon } from '../fa-icon'
import { TAG_PRESET_COLORS } from '../tag-color-utils'
import { Popover, PopoverContent, PopoverTrigger } from '../ui'
import { CustomColorPanel } from './custom-color-panel'

export {
  TAG_PRESET_COLORS,
  hexToHsv,
  hsvToHex,
  isValidHex,
  readableTextColor,
} from '../tag-color-utils'

interface Props extends Omit<ComponentProps<'div'>, 'onChange'> {
  value: string
  onChange(value: string): void
  presets?: string[]
}

export const TagColorPicker: React.FC<Props> = ({
  className,
  value,
  onChange,
  presets = TAG_PRESET_COLORS,
  ...rest
}) => {
  const isPreset = presets.includes(value.toLowerCase())

  return (
    <div className={cn('flex items-center gap-2', className)} {...rest}>
      {presets.map(color => (
        <button
          key={color}
          type="button"
          className={cn(
            'size-6 rounded-full cursor-pointer border border-transparent',
            value.toLowerCase() === color && 'ring-2 ring-offset-1 ring-primary'
          )}
          style={{ backgroundColor: color }}
          aria-label={`Color ${color}`}
          onClick={() => onChange(color)}
        />
      ))}

      <span className="mx-1 h-5 w-px bg-gray-300" />

      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(
              'size-6 rounded-full cursor-pointer border border-gray-200',
              !isPreset && 'ring-2 ring-offset-1 ring-primary'
            )}
            style={{
              background: !isPreset
                ? value
                : 'conic-gradient(red, yellow, lime, aqua, blue, magenta, red)',
            }}
            aria-label="Custom color"
          >
            {isPreset && (
              <FAIcon icon="palette" className="text-white text-xs" />
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent
          className="p-0 w-auto"
          align="end"
          onMouseUp={evt => evt.stopPropagation()}
          onTouchEnd={evt => evt.stopPropagation()}
        >
          <CustomColorPanel value={value} onChange={onChange} />
        </PopoverContent>
      </Popover>
    </div>
  )
}
